import axiosClient from './axiosClient'
import type {
  TenantConfig,
  LoginRequest,
  LoginResponse,
  CookieLoginResponse,
  GameListItem,
  GameListParams,
  ApiResponse,
  InningScore,
  TeamInfo,
  StadiumInfo,
} from '@/types'
import type {
  GameBasisData, RecordCodeItem, RecordGameInfo, RecordGameRecord, RecordPositionChangeRequest,
  RecordRosterBatch, RecordSubstitution,
} from '@/types/record'

// ── 설정 ─────────────────────────────────────────────────────
export const configApi = {
  getTenantConfig: () =>
    axiosClient.get<{ result: string; resultSet: TenantConfig }>('/config').then((r) => r.data.resultSet),
}

// ── 인증 ─────────────────────────────────────────────────────
export const authApi = {
  login: (data: LoginRequest) =>
    axiosClient.post<LoginResponse>('/auth/login', data).then((r) => r.data),
  // 쿠키 로그인(brochure) — 서버가 hiball_token 쿠키의 accessUserId로 JWT를 발급한다.
  // 실패해도 HTTP 200에 result=FALSE + resultCode로 사유를 알려준다.
  cookieLogin: () =>
    axiosClient.post<CookieLoginResponse>('/auth/cookie-login', null, { withCredentials: true }).then((r) => r.data),
  logout: () =>
    axiosClient.post('/auth/logout').then((r) => r.data),
  me: () =>
    axiosClient.get<LoginResponse['user']>('/auth/me').then((r) => r.data),
  // 아직 유효한 토큰으로 같은 유효시간의 새 토큰을 조용히 재발급한다(AuthContext의 만료 전 예약 갱신 전용).
  // 이미 만료된 토큰으로 부르면 401이다 — 그때는 재로그인(login)으로 가야 한다.
  refresh: () =>
    axiosClient.post<LoginResponse>('/auth/refresh').then((r) => r.data),
}

// ── 경기 ─────────────────────────────────────────────────────
export const gamesApi = {
  // 경기 목록 — 기록입력으로 들어가는 동선의 출발점 (hiball/biz/record/gamelist/*)
  list: ({ yearMonth, gameTypeCode, teamId }: GameListParams) => {
    const [season, month] = yearMonth.split('/')
    return axiosClient.get<ApiResponse<{ resultList: GameListItem[] }>>('/v1/record/games', {
      params: {
        season,
        month,
        ...(gameTypeCode && { gameType: gameTypeCode }),
        ...(teamId && { teamId }),
      },
    }).then((r) => r.data)
  },
  // 경기 상세 — 목록 표시용 필드 + 이닝별 점수
  detail: (gameInfoId: string | number) =>
    axiosClient.get<ApiResponse<{ gameInfo: GameListItem; inningScores: InningScore[] }>>(
      `/v1/record/games/${gameInfoId}/detail`
    ).then((r) => r.data),
}

export const recordApi = {
  // 기록입력 화면 기본데이터: 경기정보 + 로스터 + 포지션이력 + 참가선수 + 팀명
  getBasisData: (gameInfoId: string | number) =>
    axiosClient.get<ApiResponse<GameBasisData>>(`/v1/record/games/${gameInfoId}`).then((r) => r.data),
  // 저장된 투구 기록 전체 (이닝/타순/투구순)
  getRecords: (gameInfoId: string | number) =>
    axiosClient.get<ApiResponse<{ records: RecordGameRecord[] }>>(
      `/v1/record/games/${gameInfoId}/records`
    ).then((r) => r.data),
  // 투구 1건 저장 (gameRecordId 기준 멱등, 오프라인 재전송 안전)
  savePitch: (gameInfoId: string | number, record: RecordGameRecord) =>
    axiosClient.post<ApiResponse<{ recordCnt: number }>>(
      `/v1/record/games/${gameInfoId}/records`, record
    ).then((r) => r.data),
  // 저장된 투구 기록 수정 (gameRecordId 기준 부분 UPDATE, cascade 영향받는 이후 기록 포함 목록으로 전달)
  updateRecords: (gameInfoId: string | number, records: RecordGameRecord[]) =>
    axiosClient.put<ApiResponse<{ updatedCnt: number }>>(
      `/v1/record/games/${gameInfoId}/records`, records
    ).then((r) => r.data),
  // 투구 기록 교체 ("서버로 데이터 보내기") — innings를 넘기면 **그 이닝(초/말)만** 지우고 이 목록으로
  // 다시 넣는다(다른 이닝의 서버 기록은 그대로 둔다). innings를 생략하면(기존 동작) 그 경기의 서버 투구
  // 기록을 전부 지우고 이 목록으로 다시 넣는다. 선수교체(GAME_ROSTER)·포지션 이동은 이 API 범위 밖이다(별도 진행).
  replaceRecords: (
    gameInfoId: string | number,
    records: RecordGameRecord[],
    innings?: { inning: number; inningTb: number }[],
  ) =>
    axiosClient.put<ApiResponse<{ savedCnt: number }>>(
      `/v1/record/games/${gameInfoId}/records/replace`, { records, innings }
    ).then((r) => r.data),
  // 투구 1건 삭제 — 타석 상세 레이어의 투구 삭제 기능. 타석결과가 있는 마지막 투구는 프론트엔드가 삭제 자체를 막는다.
  deleteRecord: (gameInfoId: string | number, gameRecordId: string) =>
    axiosClient.delete<ApiResponse<{ deletedCnt: number }>>(
      `/v1/record/games/${gameInfoId}/records/${gameRecordId}`
    ).then((r) => r.data),
  // 선수 교체 (대타/대주자/대수비/투수교체) — 새 로스터 행 생성 + 나간 선수 출전구간 종료
  saveSubstitution: (gameInfoId: string | number, body: RecordSubstitution) =>
    axiosClient.post<ApiResponse<{ gameRosterId: number }>>(
      `/v1/record/games/${gameInfoId}/rosters`, body
    ).then((r) => r.data),
  // 라인업 변경 일괄 저장 — 같은 시점의 교체·포지션이동을 한 트랜잭션으로 적용한다(부분 반영 없음).
  // 더블 스위치처럼 여러 선수가 한 순간에 바뀌는 경우에 쓴다(프론트의 lineupDiff 산출 결과를 그대로 보냄).
  saveRosterBatch: (gameInfoId: string | number, body: RecordRosterBatch) =>
    axiosClient.post<ApiResponse<{ appliedCnt: number }>>(
      `/v1/record/games/${gameInfoId}/rosters/batch`, body
    ).then((r) => r.data),
  // 네이버 문자중계 원문(패스스루) — 브라우저에서 중계 서버를 직접 부르면 CORS 로 막혀서 서버가 대신 받는다.
  // 해석은 프론트엔드 `relaySubstitutions.ts` 가 한다(자동 입력 CLI 와 같은 모듈).
  // ⚠️ `inning` 을 빼면 마지막 이닝만 온다 — 이닝별로 받아 합쳐야 한다.
  getNaverRelay: (gameInfoId: string | number, naverGameId: string, inning: number) =>
    axiosClient.get<ApiResponse<{ result?: { textRelayData?: unknown } }>>(
      `/v1/record/games/${gameInfoId}/relay`, { params: { naverGameId, inning } }
    ).then((r) => r.data),
  // 라인업 전체 원복(선발 상태로) — "문자중계로 경기 전체 반영"을 이미 실행한 경기에 확인 절차를 거쳐
  // 다시 실행할 때 쓴다. 선수교체를 벤치로 되돌리고 POSITION_CHANGE_HISTORY를 전량 삭제한다.
  // 투구 기록은 건드리지 않는다(뒤이은 replaceRecords가 경기 단위로 전량 교체하면서 처리한다).
  resetRosterToStart: (gameInfoId: string | number) =>
    axiosClient.post<ApiResponse<{
      resetRosterCnt: number; reopenedRosterCnt: number; deletedPositionHistoryCnt: number
    }>>(`/v1/record/games/${gameInfoId}/rosters/reset`).then((r) => r.data),
  // 교체 없이 이미 뛰고 있는 선수의 수비위치만 이동 (GAME_ROSTER는 그대로, POSITION_CHANGE_HISTORY만 추가)
  savePositionChange: (gameInfoId: string | number, body: RecordPositionChangeRequest) =>
    axiosClient.post<ApiResponse<unknown>>(
      `/v1/record/games/${gameInfoId}/position-changes`, body
    ).then((r) => r.data),
  // 경기 완료 처리 — 'Y' 완료 / 'P' 진행중으로 되돌리기. 완료 시 서버가 점수를 다시 계산한다.
  completeGame: (gameInfoId: string | number, completeYn: 'Y' | 'P') =>
    axiosClient.put<ApiResponse<RecordGameInfo>>(
      `/v1/record/games/${gameInfoId}/complete`, { completeYn }
    ).then((r) => r.data),
  // 점수 재계산 배치 — 기록 수정(PUT)은 점수를 건드리지 않으므로, 이닝 스킵·기록 수정 뒤
  // RUNNERS 기준으로 투구별 점수 스냅샷 + GAME_INFO 점수를 다시 확정할 때 호출한다.
  recalculateScores: (gameInfoId: string | number) =>
    axiosClient.post<ApiResponse<{ updatedCnt: number }>>(
      `/v1/record/games/${gameInfoId}/scores/recalculate`
    ).then((r) => r.data),
  // CODES 그룹(P_CODE_ID) 목록 조회 — 예: getCodes([201000, 202000]) → 작전+메모 코드
  getCodes: (groups: number[]) =>
    axiosClient.get<ApiResponse<{ codes: RecordCodeItem[] }>>(
      '/v1/record/codes', { params: { groups: groups.join(',') } }
    ).then((r) => r.data),
}

// ── 경기 확장 ─────────────────────────────────────────────────
export const gameViewApi = {
  teams: () =>
    axiosClient.get<ApiResponse<{ teamList: TeamInfo[] }>>('/v1/record/teams').then((r) => r.data),

  stadiums: () =>
    axiosClient.get<ApiResponse<{ stadiumList: StadiumInfo[] }>>('/v1/record/stadiums').then((r) => r.data),
}
